"use client";

/**
 * 비밀번호 변경 다이얼로그
 *
 * 마이페이지 내 계정 섹션에서 트리거.
 * 별도 페이지 없이 다이얼로그로 처리.
 */
import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/shared/auth/hooks";
import { FieldError } from "@/shared/form/FieldError";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface PasswordChangeDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function PasswordChangeDialog({
  children,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}: PasswordChangeDialogProps) {
  const { apiClient } = useAuth();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen ?? internalOpen;
  const [fields, setFields] = useState({ newPwd: "", confirmPwd: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isMatch =
    fields.newPwd.length > 0 && fields.confirmPwd.length > 0 && fields.newPwd === fields.confirmPwd;

  const showLengthHint = fields.newPwd.length > 0 && fields.newPwd.length < 8 && !errors.newPwd;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!fields.newPwd) errs.newPwd = "새 비밀번호를 입력해주세요.";
    else if (fields.newPwd.length < 8) errs.newPwd = "비밀번호는 8자 이상이어야 합니다.";
    if (!fields.confirmPwd) errs.confirmPwd = "비밀번호 확인을 입력해주세요.";
    else if (fields.newPwd !== fields.confirmPwd) errs.confirmPwd = "비밀번호가 일치하지 않습니다.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting || !validate()) return;

    setIsSubmitting(true);
    try {
      await apiClient.put("/users/password", {
        futurePw: fields.newPwd,
        futureConfirmPw: fields.confirmPwd,
      });
      toast.success("비밀번호가 변경되었습니다.");
      setFields({ newPwd: "", confirmPwd: "" });
      setErrors({});
      handleOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "비밀번호 변경에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleOpenChange(next: boolean) {
    (externalOnOpenChange ?? setInternalOpen)(next);
    if (!next) {
      setFields({ newPwd: "", confirmPwd: "" });
      setErrors({});
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>비밀번호 변경</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="dlg-newPwd" className="text-sm font-medium text-neutral-700">
              새 비밀번호
            </Label>
            <Input
              id="dlg-newPwd"
              name="newPwd"
              type="password"
              placeholder="8자 이상 입력하세요"
              value={fields.newPwd}
              onChange={handleChange}
              autoComplete="new-password"
              className="h-10 rounded-md"
            />
            {errors.newPwd && <FieldError message={errors.newPwd} />}
            {showLengthHint && (
              <p className="text-xs text-muted-foreground">비밀번호는 8자 이상이어야 합니다</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dlg-confirmPwd" className="text-sm font-medium text-neutral-700">
              비밀번호 확인
            </Label>
            <Input
              id="dlg-confirmPwd"
              name="confirmPwd"
              type="password"
              placeholder="비밀번호를 다시 입력하세요"
              value={fields.confirmPwd}
              onChange={handleChange}
              autoComplete="new-password"
              className="h-10 rounded-md"
            />
            {errors.confirmPwd && <FieldError message={errors.confirmPwd} />}
            {isMatch && (
              <div className="flex items-center gap-1.5 rounded-md bg-success/10 px-3 py-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                <p className="text-sm font-medium text-success">비밀번호가 일치합니다</p>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="h-10 w-full bg-primary font-medium text-white hover:bg-primary/90"
            disabled={!isMatch || isSubmitting}
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            비밀번호 변경
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
